import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Inbox as InboxIcon, ListChecks, Mail, Sparkles, ShieldX, SlidersHorizontal } from "lucide-react";
import { TrayList } from "@/components/bd/TrayList";
import { ScreenedList } from "@/components/bd/ScreenedList";
import { JudgmentList } from "@/components/bd/JudgmentList";
import { ReplayPanel } from "@/components/bd/ReplayPanel";
import { RulesEditor } from "@/components/bd/RulesEditor";
import { HealthPanel } from "@/components/bd/HealthPanel";
import { SuggestionList } from "@/components/bd/SuggestionList";
import Inbox from "@/pages/Inbox";

const TABS = ["inbox", "tray", "suggestions", "screened", "rules", "health"];

function Badge({ n }) {
  if (!n) return null;
  return <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center">{n}</span>;
}

function HealthLine({ h }) {
  if (!h) return null;
  const f = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const acc = h.accuracy_7d;
  const ai = h.ai_calls_24h;
  return (
    <Card className="p-3 flex items-center gap-x-6 gap-y-1 flex-wrap text-xs text-slate-600" data-testid="bd-health-line">
      <span className="inline-flex items-center gap-1"><Activity className="w-3.5 h-3.5 text-[#003E87]" /> <span className={h.enabled ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{h.enabled ? `On · ${h.mode}` : "Off"}</span></span>
      <span>Last email received <b>{f(h.last_email_received)}</b></span>
      <span>Last judged <b>{f(h.last_email_judged)}</b></span>
      <span>Waiting <b>{h.waiting}</b></span>
      <span className={h.failures_24h?.length ? "text-rose-700" : ""}>Failures 24h <b>{h.failures_24h?.length || 0}</b></span>
      {ai && <span data-testid="bd-ai-calls" className={ai.emails_with_more_than_one_call?.length ? "text-rose-700" : ""}>AI calls 24h <b>{ai.ai_calls}</b> on <b>{ai.emails}</b> emails{ai.emails_with_more_than_one_call?.length ? ` · ${ai.emails_with_more_than_one_call.length} read twice` : " · one each"}</span>}
      <span data-testid="bd-accuracy">7-day: {acc?.judgments || 0} judged · {acc?.corrections || 0} corrected · {acc?.accuracy != null ? `${Math.round(acc.accuracy * 100)}% right` : "—"}</span>
      {h.legacy_reader && <span className="text-amber-700 font-semibold" data-testid="bd-legacy-on">Old reader on</span>}
    </Card>
  );
}

export default function EmailAgent() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.includes(sp.get("tab")) ? sp.get("tab") : "inbox";
  const [health, setHealth] = useState(null);
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const refresh = () => { api.get("/bd/health").then((r) => setHealth(r.data)).catch(() => {}); };
  useEffect(() => { refresh(); }, []);
  const setTab = (v) => { const q = new URLSearchParams(sp); q.set("tab", v); q.delete("sub"); setSp(q); };
  return (
    <div className="px-6 sm:px-8 py-8 max-w-[1800px] w-full mx-auto" data-testid="email-agent-page">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-[#003E87]" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-black">Email Agent</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">One reader, one log. Every email is read once, judged against the fit rules and filed on its pursuit; replies owed, new leads and suggestions wait here for a person's click.</p>
      <HealthLine h={health} />
      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="inbox" data-testid="bd-tab-inbox"><InboxIcon className="w-3.5 h-3.5 mr-1" /> Inbox</TabsTrigger>
          <TabsTrigger value="tray" data-testid="bd-tab-tray"><Sparkles className="w-3.5 h-3.5 mr-1" /> New leads<Badge n={health?.tray_open} /></TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="bd-tab-suggestions"><ListChecks className="w-3.5 h-3.5 mr-1" /> Suggestions<Badge n={health?.suggestions_pending} /></TabsTrigger>
          <TabsTrigger value="screened" data-testid="bd-tab-screened"><ShieldX className="w-3.5 h-3.5 mr-1" /> Screened out</TabsTrigger>
          <TabsTrigger value="rules" data-testid="bd-tab-rules"><SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Fit rules</TabsTrigger>
          <TabsTrigger value="health" data-testid="bd-tab-health"><Activity className="w-3.5 h-3.5 mr-1" /> Health</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-4"><Inbox embedded /></TabsContent>
        <TabsContent value="tray" className="mt-4"><TrayList onChanged={refresh} /></TabsContent>
        <TabsContent value="suggestions" className="mt-4"><SuggestionList onChanged={refresh} /></TabsContent>
        <TabsContent value="screened" className="mt-4"><ScreenedList onChanged={refresh} /></TabsContent>
        <TabsContent value="rules" className="mt-4"><RulesEditor canEdit={canEdit} /></TabsContent>
        <TabsContent value="health" className="mt-4 space-y-6">
          <HealthPanel canRun={canEdit} />
          <section data-testid="bd-health-judgments"><h2 className="text-base md:text-lg font-semibold text-black mb-2">Judgments</h2><JudgmentList onChanged={refresh} /></section>
          <section data-testid="bd-health-replay"><h2 className="text-base md:text-lg font-semibold text-black mb-2">Replay &amp; clean-up</h2><ReplayPanel canRun={canEdit} /></section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
